
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PriceCategory } from "@/types/priceCategory";
import { useState } from "react";

interface NewPriceCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (category: Omit<PriceCategory, 'id' | 'created_at' | 'updated_at'>) => void;
}

export function NewPriceCategoryDialog({
  open,
  onClose,
  onSave,
}: NewPriceCategoryDialogProps) {
  const [newCategory, setNewCategory] = useState<Omit<PriceCategory, 'id' | 'created_at' | 'updated_at'>>({
    name: '',
    prefix: '',
    description: '',
    price_type: 'fixed',
    fixed_price: 0,
    min_price: undefined,
    max_price: undefined,
    active: true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(newCategory);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo Grupo de Preço</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="prefix">Prefixo</Label>
            <Input
              id="prefix"
              value={newCategory.prefix}
              onChange={(e) =>
                setNewCategory({
                  ...newCategory,
                  prefix: e.target.value.toUpperCase(),
                })
              }
              maxLength={3}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Nome do Grupo</Label>
            <Input
              id="name"
              value={newCategory.name}
              onChange={(e) =>
                setNewCategory({
                  ...newCategory,
                  name: e.target.value,
                })
              }
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Tipo de Preço</Label>
            <RadioGroup
              value={newCategory.price_type}
              onValueChange={(value) =>
                setNewCategory({
                  ...newCategory,
                  price_type: value as 'fixed' | 'range',
                  fixed_price: value === 'fixed' ? newCategory.fixed_price : null,
                  min_price: value === 'range' ? newCategory.min_price || 0 : undefined,
                  max_price: value === 'range' ? newCategory.max_price || 0 : undefined,
                })
              }
              className="flex space-x-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="fixed" id="fixed" />
                <Label htmlFor="fixed">Preço Fixo</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="range" id="range" />
                <Label htmlFor="range">Faixa de Preço</Label>
              </div>
            </RadioGroup>
          </div>

          {newCategory.price_type === 'fixed' ? (
            <div className="space-y-2">
              <Label htmlFor="price">Preço Fixo ($)</Label>
              <Input
                id="price"
                type="number"
                value={newCategory.fixed_price || 0}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    fixed_price: parseFloat(e.target.value),
                  })
                }
                required
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min_price">Preço Mínimo ($)</Label>
                <Input
                  id="min_price"
                  type="number"
                  value={newCategory.min_price || 0}
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      min_price: parseFloat(e.target.value),
                    })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_price">Preço Máximo ($)</Label>
                <Input
                  id="max_price"
                  type="number"
                  value={newCategory.max_price || 0}
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      max_price: parseFloat(e.target.value),
                    })
                  }
                  required
                />
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button type="submit">Salvar</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
